import type { GridColDef, GridColumnVisibilityModel, GridValidRowModel } from '@mui/x-data-grid-pro';

/** Shape serialized into the `{settingsKey}.datagrid.columns` user setting (LB-4081, LB-4992). */
export interface IColumnsSetting {
  /** User column fields in display order (hidden ones included). */
  order: string[];
  /** Fields the user has hidden. */
  hidden: string[];
  /** Widths (px) of the columns the user has resized; untouched columns keep flex sizing. */
  widths?: Record<string, number>;
}

export const COLUMNS_SETTING_SUFFIX = '.datagrid.columns';

// MUI service columns (checkbox, row reorder, detail panel toggle, tree grouping) are not user
// columns: they keep their position in the grid and are never written into the setting.
const SERVICE_FIELD_PREFIX = '__';

export const isUserField = (field: string): boolean => !field.startsWith(SERVICE_FIELD_PREFIX);

const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every((item) => typeof item === 'string');

const isWidths = (value: unknown): value is Record<string, number> | undefined =>
  value === undefined
  || (typeof value === 'object' && value !== null && Object.values(value).every((width) => typeof width === 'number'));

export const isColumnsSetting = (value: unknown): value is IColumnsSetting => {
  const candidate = value as Partial<IColumnsSetting> | null;

  return typeof candidate === 'object' && candidate !== null
    && isStringArray(candidate.order) && isStringArray(candidate.hidden) && isWidths(candidate.widths);
};

/**
 * Pure transform of the `columns` prop: saved fields first in the saved order, fields unknown to the
 * setting after them in their natural order, service columns at their original index. A saved width
 * makes the column fixed (`flex` is dropped, it wins over `width`); saved fields that no longer exist are ignored.
 */
export const applyColumnsSetting = <T extends GridValidRowModel>(
  columns: readonly GridColDef<T>[],
  saved: IColumnsSetting | undefined,
): readonly GridColDef<T>[] => {
  if (!saved) {
    return columns;
  }

  const applyWidth = (column: GridColDef<T>): GridColDef<T> => {
    const width = saved.widths?.[column.field];

    return width ? { ...column, width, flex: undefined } : column;
  };

  const orderIndex = new Map(saved.order.map((field, index) => [field, index]));
  const userColumns = columns.filter((column) => isUserField(column.field));
  const known = userColumns
    .filter((column) => orderIndex.has(column.field))
    .sort((a, b) => orderIndex.get(a.field)! - orderIndex.get(b.field)!);
  const unknown = userColumns.filter((column) => !orderIndex.has(column.field));
  const ordered = [...known, ...unknown].map(applyWidth);

  columns.forEach((column, index) => {
    if (!isUserField(column.field)) {
      ordered.splice(index, 0, column);
    }
  });

  return ordered;
};

/** Explicit entries for every saved field: the saved visibility overrides colDef defaults. */
export const toColumnVisibilityModel = (saved: IColumnsSetting | undefined): GridColumnVisibilityModel | undefined =>
  saved && Object.fromEntries(saved.order.map((field) => [field, !saved.hidden.includes(field)]));

export const toColumnsSetting = (
  orderedFields: readonly string[],
  visibilityModel: GridColumnVisibilityModel,
  widths: Record<string, number>,
): IColumnsSetting => {
  const order = orderedFields.filter(isUserField);

  return { order, hidden: order.filter((field) => visibilityModel[field] === false), widths };
};
